const ATTITUDE_FACTORS = {
  PIC: 4 / 3,
  PIN: 1,
  MOTS: 0.515,
}

const PERFORMANCE_FORMULAS = {
  MOTS: teamSpirit =>
    -0.0055 * teamSpirit * teamSpirit + 0.1676 * teamSpirit + 0.4585,
  PIN: teamSpirit =>
    -0.0051 * teamSpirit * teamSpirit + 0.1521 * teamSpirit + 0.4189,
  PIC: teamSpirit =>
    -0.0043 * teamSpirit * teamSpirit + 0.1279 * teamSpirit + 0.3587,
}

const pad2 = value => String(value).padStart(2, '0')

export const normalizeAttitude = attitude => {
  const rawKey = String(attitude || 'PIC').toUpperCase()
  const key = rawKey === 'NORM' ? 'PIN' : rawKey
  return ATTITUDE_FACTORS[key] ? key : 'PIC'
}

export const parseNumber = value => {
  const normalized = String(value ?? '')
    .trim()
    .replace(',', '.')
  if (normalized === '') return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

export const roundSpirit = value => {
  if (!Number.isFinite(value)) return null
  return Math.max(0, Number(value.toFixed(6)))
}

export const formatSpirit = value => {
  const parsed = parseNumber(value)
  if (parsed === null) return '—'
  return parsed.toFixed(2)
}

export const formatCoefficient = value => {
  const parsed = parseNumber(value)
  if (parsed === null) return '—'
  return parsed.toFixed(3)
}

export const formatDateLabel = date => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—'
  return `${date.getDate()}.${date.getMonth() + 1}.`
}

export const createDateFromInput = value => {
  if (!value) return new Date()
  const date = new Date(`${value}T12:00:00`)
  if (Number.isNaN(date.getTime())) return new Date()
  return date
}

export const toDateInputValue = date => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}`
}

export const addDays = (date, days) => {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

export const calculateSpiritAfterUpdate = spirit => {
  const current = parseNumber(spirit)
  if (current === null) return null

  let drop = 0
  if (current <= 7.5) {
    drop = 0.00422023 * current * current + 0.00313087 * current - 0.1188
  } else if (current < 9) {
    drop = 0.013333 * current * current - 0.1666 * current + 0.64
  } else {
    drop = 0.0039 * current * current + 0.042 * current - 0.459
  }

  return roundSpirit(current - drop)
}

export const calculateResetSpirit = (spirit, remainingFactor) => {
  const current = parseNumber(spirit)
  const factor = parseNumber(remainingFactor)
  if (current === null || factor === null) return null
  return roundSpirit((current - 5) * factor + 5)
}

export const advanceSpirit = (startingSpirit, updates) => {
  let spirit = parseNumber(startingSpirit)
  if (spirit === null) return null

  const count = Number(updates) || 0
  for (let index = 0; index < count; index += 1) {
    spirit = calculateSpiritAfterUpdate(spirit)
    if (spirit === null) return null
  }

  return spirit
}

export const applyAttitudeToSpirit = (spirit, attitude) => {
  const currentSpirit = parseNumber(spirit)
  const normalizedAttitude = normalizeAttitude(attitude)
  if (currentSpirit === null) return null
  return roundSpirit(currentSpirit * ATTITUDE_FACTORS[normalizedAttitude])
}

export const inferAttitudeFromSpirits = (spiritBefore, spiritAfter) => {
  const before = parseNumber(spiritBefore)
  const after = parseNumber(spiritAfter)
  if (before === null || after === null) return null

  // Only PIC can increase team spirit. Confirmed values can be rounded, so
  // resolve this before the nearest-value comparison below.
  if (after > before) return 'PIC'

  return ['PIC', 'PIN', 'MOTS']
    .map(attitude => ({
      attitude,
      predictedSpirit: applyAttitudeToSpirit(before, attitude),
    }))
    .filter(candidate => candidate.predictedSpirit !== null)
    .sort((left, right) => {
      const leftDifference = Math.abs(Math.min(10, left.predictedSpirit) - after)
      const rightDifference = Math.abs(Math.min(10, right.predictedSpirit) - after)
      return leftDifference - rightDifference
    })[0]?.attitude || null
}

export const getPerformanceCoefficient = (attitude, spirit) => {
  const currentSpirit = parseNumber(spirit)
  const normalizedAttitude = normalizeAttitude(attitude)
  if (currentSpirit === null) return null
  return roundSpirit(PERFORMANCE_FORMULAS[normalizedAttitude](currentSpirit))
}

export const createDefaultMatchRows = count =>
  Array.from({ length: Math.max(0, Number(count) || 0) }, (_, index) => ({
    id: index,
    attitude: 'PIC',
    midfieldInput: '',
    manualSpiritBeforeInput: '',
  }))

export const buildTeamSpiritTimeline = ({
  startDate,
  startingSpirit,
  matchRows,
  resetEvents,
}) => {
  const matches = Array.isArray(matchRows) ? matchRows : []
  const resets = Array.isArray(resetEvents) ? resetEvents : []
  const timeline = []
  const baseDate = createDateFromInput(startDate)
  let currentSpirit = parseNumber(startingSpirit)

  if (currentSpirit === null) {
    currentSpirit = 5
  }

  const baseMatch = matches.find(matchRow => matchRow.schedule) || null
  const baseSlot = baseMatch?.schedule?.slot ?? 0
  const events = [
    ...matches
      .filter(matchRow => matchRow.schedule)
      .map((matchRow, index) => ({
        type: 'match',
        key: `match-${index}`,
        slot: matchRow.schedule.slot,
        matchRow,
      })),
    ...resets
      .filter(resetEvent => Number.isFinite(Number(resetEvent?.slot)))
      .map(resetEvent => ({
        type: 'reset',
        key: resetEvent.key,
        slot: Number(resetEvent.slot),
        label: resetEvent.label,
        resetFactor: resetEvent.resetFactor,
        nationalTargets: resetEvent.nationalTargets || null,
        worldTargets: resetEvent.worldTargets || null,
      })),
  ]
    .filter(event => event.slot >= baseSlot)
    .sort((a, b) => a.slot - b.slot || (a.type === 'match' ? -1 : 1))

  const matchSnapshots = []

  events.forEach((event, index) => {
    const eventDate = addDays(baseDate, event.slot - baseSlot)

    if (event.type === 'match') {
      const matchRow = event.matchRow
      const attitude = normalizeAttitude(matchRow.attitude)
      const manualSpiritBeforeMatch = parseNumber(
        matchRow.manualSpiritBeforeInput
      )
      const spiritBeforeMatch =
        manualSpiritBeforeMatch !== null
          ? manualSpiritBeforeMatch
          : currentSpirit
      const coefficient = getPerformanceCoefficient(attitude, spiritBeforeMatch)
      const spiritAfterAttitude = applyAttitudeToSpirit(
        spiritBeforeMatch,
        attitude
      )
      const nextEvent = events[index + 1] || null
      const daysUntilNext = nextEvent ? nextEvent.slot - event.slot : 0
      const visibleUpdates = nextEvent
        ? nextEvent.type === 'reset'
          ? Math.max(0, daysUntilNext)
          : Math.max(0, daysUntilNext)
        : 0

      let spiritAfterUpdates = spiritAfterAttitude
      const updateRows = []

      for (
        let updateIndex = 1;
        updateIndex <= daysUntilNext;
        updateIndex += 1
      ) {
        spiritAfterUpdates = calculateSpiritAfterUpdate(spiritAfterUpdates)
        if (updateIndex <= visibleUpdates) {
          updateRows.push({
            type: 'update',
            key: `update-${event.key}-${updateIndex}`,
            date: addDays(eventDate, updateIndex),
            spirit: spiritAfterUpdates,
          })
        }
      }

      const matchSnapshot = {
        type: 'match',
        key: event.key,
        index: Number.isFinite(Number(matchRow.id))
          ? Number(matchRow.id)
          : index,
        date: eventDate,
        schedule: matchRow.schedule || null,
        attitude,
        midfieldInput: matchRow.midfieldInput,
        manualSpiritBeforeInput: matchRow.manualSpiritBeforeInput,
        spiritBeforeMatch,
        hasManualSpiritBefore: manualSpiritBeforeMatch !== null,
        spiritAfterAttitude,
        spiritAfterUpdates,
        coefficient,
        updatesUntilNext: visibleUpdates,
        updateRows,
        estimatedNextMidfield: null,
        estimatedMatchMidfield: null,
      }

      matchSnapshots.push(matchSnapshot)
      timeline.push(matchSnapshot)
      updateRows.forEach(updateRow => timeline.push(updateRow))
      currentSpirit = spiritAfterUpdates
      return
    }

    const resetSpiritBefore = currentSpirit
    const resetSpiritAfter = calculateResetSpirit(
      resetSpiritBefore,
      event.resetFactor
    )

    timeline.push({
      type: 'reset',
      key: event.key,
      date: eventDate,
      label: event.label,
      resetFactor: event.resetFactor,
      nationalTargets: event.nationalTargets || null,
      worldTargets: event.worldTargets || null,
      spiritBeforeReset: resetSpiritBefore,
      spiritAfterReset: resetSpiritAfter,
    })

    currentSpirit = resetSpiritAfter
  })

  matchSnapshots.forEach((snapshot, index) => {
    const nextSnapshot = matchSnapshots[index + 1]
    const currentMidfield = parseNumber(snapshot.midfieldInput)
    if (
      nextSnapshot &&
      currentMidfield !== null &&
      snapshot.coefficient !== null &&
      nextSnapshot.coefficient !== null &&
      snapshot.coefficient !== 0
    ) {
      const estimatedNextMidfield = roundSpirit(
        currentMidfield * (nextSnapshot.coefficient / snapshot.coefficient)
      )
      snapshot.estimatedNextMidfield = estimatedNextMidfield
      nextSnapshot.estimatedMatchMidfield = estimatedNextMidfield
    }
  })

  return timeline
}
